import { useMemo } from 'react';
import { Card } from '@/shared/components/ui/Card';
import { CLOSED_STAGES, formatMoney } from '../constants';
import type { Deal } from '../types';

interface CurrencyTotals {
  currency: string;
  activeValue: number;
  wonValue: number;
  count: number;
}

/**
 * Pipeline value summary, ALWAYS grouped by currency. Mixed currencies are
 * never summed together. When only one currency is present, a single summary
 * group is shown automatically.
 */
export function PipelineSummary({ deals }: { deals: Deal[] }) {
  const groups = useMemo<CurrencyTotals[]>(() => {
    const map = new Map<string, CurrencyTotals>();
    for (const d of deals) {
      const currency = d.currency || 'INR';
      const g =
        map.get(currency) ?? { currency, activeValue: 0, wonValue: 0, count: 0 };
      const value = Number(d.value ?? 0);
      g.count += 1;
      if (d.stage === 'closed_won') g.wonValue += value;
      if (!CLOSED_STAGES.includes(d.stage)) g.activeValue += value;
      map.set(currency, g);
    }
    return Array.from(map.values()).sort((a, b) => b.activeValue - a.activeValue);
  }, [deals]);

  if (deals.length === 0) return null;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-display-sm font-semibold text-on-surface">{deals.length}</p>
          <p className="mt-1 text-body-sm text-on-surface-variant">Total Deals</p>
        </Card>
      </div>

      {/* Per-currency value groups — never aggregated together */}
      <div className="space-y-2">
        {groups.map((g) => (
          <div
            key={g.currency}
            className="flex flex-wrap items-center gap-x-6 gap-y-2 rounded-card border border-outline bg-surface px-4 py-3"
          >
            <span className="text-label-caps uppercase text-on-surface-variant">{g.currency}</span>
            <div className="flex flex-col">
              <span className="text-data-lg font-semibold text-on-surface">
                {formatMoney(g.activeValue, g.currency)}
              </span>
              <span className="text-body-sm text-on-surface-variant">Active pipeline</span>
            </div>
            <div className="flex flex-col">
              <span className="text-data-lg font-semibold text-success">
                {formatMoney(g.wonValue, g.currency)}
              </span>
              <span className="text-body-sm text-on-surface-variant">Won</span>
            </div>
            <div className="flex flex-col">
              <span className="text-data-lg font-semibold text-on-surface">{g.count}</span>
              <span className="text-body-sm text-on-surface-variant">Deals</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
